/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 93 — read-only snapshot of the logged-in user's live presence
// state (city, battery, lastUpdatedAt). Used by useSafeCircleStaticMembers
// to overlay MockFriend's view-model so the demo tile shows the user's
// own real data instead of fabricated sample values. This is NOT a
// presence writer — no RTDB writes happen here. When the Phase 2
// real-member presence layer ships, it will write the same shape into
// /users/{uid}/presence and circle screens will read it from there.

import { useEffect, useState } from "react";
import { Device } from "@capacitor/device";
import { useGeolocation } from "./useGeolocation";
import { reverseGeocodeCity } from "@/utils/reverseGeocode";
import { batteryPctFromLevel } from "@/utils/batteryLevel";
import type { MemberStatus } from "@/utils/safecircle/mockFriend";

export interface UserPresenceSnapshot {
  status: MemberStatus;
  city?: string;
  batteryPct?: number;
  isCharging?: boolean;
  lastUpdatedAt?: number;
}

// Min distance (km) the user must travel before we re-run reverse-geocode.
// Google billing meter — only refresh on meaningful moves, not every
// watchPosition tick.
const CITY_REFRESH_KM = 0.5;

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function useUserPresenceSnapshot(): UserPresenceSnapshot {
  const { lat, lng } = useGeolocation();

  const [city, setCity] = useState<string | undefined>(undefined);
  const [lastCityCoord, setLastCityCoord] = useState<{ lat: number; lng: number } | null>(null);
  const [battery, setBattery] = useState<{ pct: number; charging: boolean } | undefined>(undefined);

  // Battery: Device.getBatteryInfo() works on iOS + Android. On web it
  // typically returns { batteryLevel: undefined, isCharging: undefined }
  // because the Battery API is disabled — we simply leave the snapshot
  // field unset and the detail card renders the placeholder.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const info = await Device.getBatteryInfo();
        if (cancelled) return;
        const pct = batteryPctFromLevel(info.batteryLevel);
        if (pct === undefined) return;
        setBattery({ pct, charging: info.isCharging === true });
      } catch {
        // Web fallback — leave undefined.
      }
    };
    void tick();
    // Refresh every 60s so the tile reflects a draining/charging device
    // without burning a watchPosition-like event stream.
    const id = window.setInterval(() => void tick(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // City: reverse-geocode the current coords on first availability, then
  // re-geocode only when the user has crossed CITY_REFRESH_KM. Each call
  // hits the Maps billing meter; the throttle is mandatory.
  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number") return;
    if (
      lastCityCoord &&
      haversineKm(lastCityCoord, { lat, lng }) < CITY_REFRESH_KM
    ) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const next = await reverseGeocodeCity(lat, lng);
      if (cancelled) return;
      if (next) {
        setCity(next);
        setLastCityCoord({ lat, lng });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, lng, lastCityCoord]);

  // lastUpdatedAt — derived from when we last had a fresh signal. Since
  // battery refreshes on a 60s interval and geo runs continuously, the
  // user's tile is effectively "up to date" whenever either has fired.
  // We expose `Date.now()` at hook-call time; the relativeTime formatter
  // upstream re-runs against the screen's own `now`.
  const lastUpdatedAt =
    typeof lat === "number" && typeof lng === "number" ? Date.now() : undefined;

  return {
    // No motion/route classifier yet — best signal we have for "the
    // user is in the app right now" is "home" (matches the "You" centre
    // node's hardcoded label). Phase 2 wires a real status pipeline.
    status: "home",
    city,
    batteryPct: battery?.pct,
    isCharging: battery?.charging,
    lastUpdatedAt,
  };
}
